import React, { useState } from 'react';
import { Link } from 'react-router-dom';

import { styled } from '@mui/material/styles';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Settings from '@mui/icons-material/Settings';
import Logout from '@mui/icons-material/Logout';
import IconButton from '@mui/material/IconButton';
import CloseOutlined from '@mui/icons-material/CloseOutlined';
import { useTranslation } from 'react-i18next';

import { Backdrop } from '@mui/material';
import { ProfileBar } from '../../profilebar/ProfileBar';
import LanguageSwitcher from '../../languageSwitcher/LanguageSwitcher';
import { MOBILE_BREAKPOINT } from '../../../utils/constants';
import { useWindowSize } from '../../../utils/hooks';

const Container = styled('div', {
  shouldForwardProp: (prop) => prop !== 'width'
})(({ width }) => ({
  width,
  display: 'flex',
  flex: 1,
  padding: '0.75rem 1rem 1rem',
  flexDirection: 'column'
}));

const ProfileBarContainer = styled('div')({
  padding: '10px 1.25rem'
});

const DefaultListItem = styled(ListItem)({
  '.MuiTypography-root': {
    fontFamily: 'Gotham-Book'
  }
});

const LogoutListItem = styled(DefaultListItem)({
  marginTop: 'auto',
  backgroundColor: '#F4F4F4'
});

const AirswiftListItem = styled(DefaultListItem)({
  '.MuiTypography-root': {
    display: 'flex',
    fontSize: '10px',
    lineHeight: '12px',
    color: '#77787B',
    justifyContent: 'center',
    fontFamily: 'Gotham Rounded'
  }
});

const DrawerHeader = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  marginBottom: '1.25rem'
});

const LanguageSwitcherContainer = styled('div')({
  padding: '8px 16px',
  display: 'flex'
});

function renderDrawerListItems({ id, label, icon, url, onClick }) {
  if (!id) return icon;
  let ListItemComponent;
  switch (id) {
    case 'logout':
      ListItemComponent = LogoutListItem;
      break;
    case 'airswift':
      ListItemComponent = AirswiftListItem;
      break;
    default:
      ListItemComponent = DefaultListItem;
      break;
  }

  return (
    <ListItemComponent key={label} disablePadding>
      <ListItemButton component={Link} to={url} onClick={onClick}>
        {icon ? <ListItemIcon>{icon}</ListItemIcon> : null}
        <ListItemText primary={label} />
      </ListItemButton>
    </ListItemComponent>
  );
}

function Drawer({ open, width, onClose, logout, className }) {
  const { t } = useTranslation();
  const drawerItems = [
    {
      id: 'settings',
      label: t('common.header.menu.settings'),
      icon: <Settings />,
      url: '/my-profile'
    },
    {
      icon: (
        <LanguageSwitcherContainer>
          <LanguageSwitcher />
        </LanguageSwitcherContainer>
      )
    },
    {
      id: 'logout',
      label: t('common.header.menu.logout'),
      icon: <Logout />,
      url: '#',
      onClick: logout
    },
    {
      id: 'airswift',
      url: '#',
      label: 'AIRSWIFT ©'
    }
  ];

  const size = useWindowSize();

  return (
    <Backdrop
      invisible={size.width > MOBILE_BREAKPOINT}
      sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}
      open={open}
      onClick={onClose}
    >
      <SwipeableDrawer
        open={open}
        className={className}
        onClose={onClose}
        disableScrollLock
        sx={{
          width,
          flexShrink: 0,
          '& .MuiDrawer-Paper': { width, boxSizing: 'border-box' }
        }}
        variant="persistent"
      >
        <Container width={width}>
          <DrawerHeader>
            <IconButton onClick={onClose}>
              <CloseOutlined />
            </IconButton>
          </DrawerHeader>
          <ProfileBarContainer>
            <ProfileBar />
          </ProfileBarContainer>
          <List sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
            {drawerItems.map(renderDrawerListItems)}
          </List>
        </Container>
      </SwipeableDrawer>
    </Backdrop>
  );
}

export default Drawer;
